import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { isAuthenticated, logoutUser } from "./modules/UserManagement/services/authService"; 
import LeadManagementPage from "./modules/LeadManagement/pages/LeadManagementPage";
import OpportunitiesPage from "./modules/Opportunities/pages/OpportunitiesPage";
import SalesPage from "./modules/Sales/pages/SalesPage";
import WorkOrdersDashboard from "./modules/WorkOrders/pages/WorkOrdersDashboard";
import ManagementFramework from "./modules/ManagementFramework/pages/ManagementFramework";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import Footer from "./components/Footer";
import WorkOrderManagement from "./modules/ManagementFramework/pages/WorkOrderManagement";
import InventoryDashboard from "./modules/InventoryManagement/pages/InventoryDashbaord";
import CustomerDashboard from "./modules/Customer/pages/CustomerDashboard";
import AddEditActivityModal from "./components/AddEditActivityModal";
import LoginPage from "./modules/UserManagement/pages/LoginPage";
import Profile from "./components/Profile";
import Settings from "./components/Settings";
import EngageHubDashboard from "./modules/EngageHub/pages/EngageHubDashboard";
import AdminPage from "./modules/Admin/pages/AdminPage";
import UserManagement from "./modules/Admin/pages/UserManagement";


function App() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(window.innerWidth < 768);
  const [isModalOpen, setIsModalOpen] = useState(false); 
  const [editingActivity, setEditingActivity] = useState(null); 
  const [authenticated, setAuthenticated] = useState(isAuthenticated()); 

  // Sample User Data (Can be fetched from API)
  const [user, setUser] = useState({
    fullName: "Shine Babu",
    email: "shine@example.com",
    role: "Admin",
  });

  // Sample Notifications for Lead Management & Activity Reminders
  const [notifications, setNotifications] = useState([
    { type: "Lead Follow-up", message: "Follow-up with John Doe for Lead Conversion", timestamp: "2025-02-08T09:00:00Z" },
    { type: "Meeting Reminder", message: "Sales meeting with ABC Corp at 3:00 PM", timestamp: "2025-02-08T14:00:00Z" },
    { type: "Task Due", message: "Complete proposal for XYZ Ltd.", timestamp: "2025-02-09T10:00:00Z" },
  ]);

  useEffect(() => {
    const handleResize = () => setIsSidebarCollapsed(window.innerWidth < 768);
  
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  // Check authentication on mount
  useEffect(() => {
    setAuthenticated(isAuthenticated());
  }, []);

  // Function to add a new notification
  const addNotification = (notification) => {
    setNotifications((prevNotifications) => [...prevNotifications, notification]);
  };

  // Function to handle saving activity from the modal
  const handleSaveActivity = (activity) => {
    console.log("Activity saved:", activity);

    // Trigger a notification if the activity includes a follow-up
    if (activity.followUp && activity.followUpDate && activity.followUpTime) {
      addNotification({
        type: "Follow-up",
        message: `Follow-up scheduled for "${activity.summary}" on ${activity.followUpDate} at ${activity.followUpTime}.`,
        timestamp: new Date().toISOString(),
      });
    }

    // Close the modal after saving
    setIsModalOpen(false);
  };

  // Handle logout
  const handleLogout = () => {
    logoutUser();
    setAuthenticated(false);
  };

  return (
    <Router>
      <div className="flex flex-col h-screen bg-gray-100">
        {/* Redirect to LoginPage if not authenticated */}
        {!authenticated ? (
          <Routes>
            <Route path="*" element={<Navigate to="/login" />} />
            <Route path="/login" element={<LoginPage setAuthenticated={setAuthenticated} />} />
          </Routes>
        ) : (
          <>
            {/* Fixed Header with User Data & Notifications */}
            <Header
              productName="iDecisionCRM"
              shopName="Jothi Rajalekshmi Curtains & Furnishings"
              notifications={notifications} // Pass notifications
              user={user} // Pass user details
              onLogout={handleLogout} 
            />

            {/* Main Layout */}
            <div className="flex flex-1 pt-16">
              {/* Static Sidebar */}
              <Sidebar
                isCollapsed={isSidebarCollapsed}
                onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              />

              {/* Main Content */}
              <div
                className={`transition-all duration-300 flex-1 p-6 overflow-auto ${
                  isSidebarCollapsed ? "ml-16" : "ml-64"
                }`}
              >
                <Routes>
                  <Route path="/" element={<Navigate to="/leads" />} />
                  <Route path="/leads" element={<LeadManagementPage />} />
                  <Route path="/opportunities" element={<OpportunitiesPage />} />
                  <Route path="/sales" element={<SalesPage />} />
                  <Route path="/workorders" element={<WorkOrdersDashboard />} />
                  <Route path="/management" element={<ManagementFramework />} />
                  <Route path="/workordermanagement" element={<WorkOrderManagement />} />
                  <Route path="/inventory" element={<InventoryDashboard />} />
                  <Route path="/customers" element={<CustomerDashboard />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/engagehub" element={<EngageHubDashboard />} />
                  
                  {/* Profile & Settings with User Data */}
                  <Route path="/profile" element={<Profile user={user} />} />
                  <Route path="/settings" element={<Settings user={user} />} />
                  <Route path="/adminpage" element={<AdminPage />} />
                  <Route path="/admin/users" element={<UserManagement />} />
                </Routes>
              </div>
            </div>

            {/* Fixed Footer */}
            <Footer />

            {/* Add/Edit Activity Modal */}
            {isModalOpen && (
              <AddEditActivityModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveActivity}
                isEditing={!!editingActivity}
                activity={editingActivity}
                addNotification={addNotification} // Pass notification handler
              />
            )}
          </>
        )}
      </div>
    </Router>
  );
}

export default App;